import { Listbox, ListboxButton, ListboxOptions } from '@headlessui/react'
import { CheckIcon, LockClosedIcon } from '@heroicons/react/20/solid'
import { GlobeAltIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useMap } from 'react-map-gl/maplibre'
import { twJoin, twMerge } from 'tailwind-merge'
import {
  defaultBackgroundParam,
  type BackgroundParam,
} from '@/components/regionen/pageRegionSlug/hooks/useQueryState/backgroundParam.const'
import { useBackgroundParam } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/useBackgroundParam'
import { useBg3dParam } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/useBg3dParam'
import { useMapParam } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/useMapParam'
import { useBreakpoint } from '@/components/shared/hooks/viewport/useBreakpoint'
import { isPrivateBackgroundParam } from '@/server/private-backgrounds/privateBackgroundParam'
import {
  mapOverlayAnchoredMenuMaxHeightClassName,
  mapOverlayMenuClassName,
} from '../mapOverlayChrome.const'
import { MobileBottomSheet } from '../mobile/MobileBottomSheet'
import {
  mapControlIconClassName,
  mobileControlButtonActiveClassName,
  mobileMapIconButtonClassName,
} from '../mobile/mobileControlButton.const'
import { Background3dToggleRow } from './Background3dToggleRow'
import { ListOption } from './ListOption'
import { useRegionBackgrounds } from './useRegionBackgrounds'

export const SelectBackground = () => {
  const { mainMap } = useMap()
  const { backgroundParam, setBackgroundParam } = useBackgroundParam()
  const { mapParam, setMapParam } = useMapParam()
  const { is3dActive, toggle3d } = useBg3dParam()
  const backgrounds = useRegionBackgrounds()
  const isSmBreakpointOrAbove = useBreakpoint('sm')
  const [sheetOpen, setSheetOpen] = useState(false)

  const onChange = (value: BackgroundParam) => {
    void setBackgroundParam(value)
  }

  const on3dChange = (active: boolean) => {
    toggle3d(active)
    if (active || !mainMap || !mapParam) return

    // Same API as MapNavigationButtons compass click (visualizePitch path).
    mainMap.getMap().resetNorthPitch({ duration: 500 })
    // Use `=== undefined` — bearing/pitch of 0 are valid and must still be stripped.
    if (mapParam.bearing === undefined && mapParam.pitch === undefined) return
    void setMapParam(
      { zoom: mapParam.zoom, lat: mapParam.lat, lng: mapParam.lng },
      { history: 'replace' },
    )
  }

  if (!mainMap) return null
  if (!backgroundParam) return null

  // Mobile: an icon button that opens the same options as a bottom sheet (consistent
  // with the other mobile controls), instead of the desktop dropdown.
  const threeDOptionsSection = (
    <div className="border-t border-gray-200 px-2 py-2">
      <p className="sr-only">3D-Optionen</p>
      <Background3dToggleRow checked={is3dActive} onChange={on3dChange} label="3D Höhenprofil" />
    </div>
  )

  if (!isSmBreakpointOrAbove) {
    const options: { value: BackgroundParam; name: string }[] = [
      ...backgrounds.map(({ id, name }) => ({ value: id, name })),
      { value: defaultBackgroundParam, name: 'Standard' },
    ]

    return (
      <section>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label="Hintergrundkarten"
          aria-expanded={sheetOpen}
          className={twMerge(
            mobileMapIconButtonClassName,
            sheetOpen && mobileControlButtonActiveClassName,
          )}
        >
          <GlobeAltIcon className={mapControlIconClassName} aria-hidden="true" />
        </button>

        <MobileBottomSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Hintergrundkarten"
        >
          {/* Container query: two columns once the sheet is wide enough (most phones).
              Rows are separated by thin divider lines (not full card borders). */}
          <div className="@container">
            <div className="grid grid-cols-1 gap-x-3 @xs:grid-cols-2">
              {options.map(({ value, name }) => {
                const selected = backgroundParam === value
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      onChange(value)
                      setSheetOpen(false)
                    }}
                    className={twJoin(
                      'flex min-w-0 items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm',
                      selected
                        ? 'bg-yellow-100 font-medium text-yellow-900'
                        : 'text-gray-900 hover:bg-yellow-50',
                    )}
                  >
                    <span className="flex w-5 shrink-0 justify-center">
                      {selected && <CheckIcon className="size-5" aria-hidden="true" />}
                    </span>
                    {/* Wrap to two lines with hyphenation instead of truncating. */}
                    <span className="line-clamp-2 leading-tight hyphens-auto" lang="de">
                      {name}
                    </span>
                    {isPrivateBackgroundParam(value) && (
                      <>
                        <LockClosedIcon
                          className="ml-auto size-4 shrink-0 text-gray-500"
                          aria-hidden="true"
                        />
                        <span className="sr-only">Nur für Mitglieder dieser Region</span>
                      </>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
          {threeDOptionsSection}
        </MobileBottomSheet>
      </section>
    )
  }

  return (
    <Listbox<'div', BackgroundParam>
      as="div"
      className="relative"
      value={backgroundParam}
      onChange={onChange}
    >
      <ListboxButton aria-label="Hintergrundkarten" className={mobileMapIconButtonClassName}>
        <GlobeAltIcon className={mapControlIconClassName} aria-hidden="true" />
      </ListboxButton>
      <ListboxOptions
        transition
        portal={false}
        anchor={false}
        className={twMerge(
          'absolute right-0 bottom-full z-30 mb-2 w-60 overflow-auto text-sm transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in',
          mapOverlayAnchoredMenuMaxHeightClassName,
          mapOverlayMenuClassName,
        )}
      >
        {backgrounds.map(({ name, id }) => {
          return <ListOption key={id} value={id} name={name} />
        })}
        <ListOption
          key={`${backgroundParam}-default`}
          value={defaultBackgroundParam}
          name="Standard"
        />
        {threeDOptionsSection}
      </ListboxOptions>
    </Listbox>
  )
}
